import { friendlyError } from "../../../lib/friendlyError";
import { trpc } from "../../../trpc";
import { TestThenSend } from "../TestThenSend";
import { emailTypeLabel } from "../emailTypes";
import {
  EMAIL_OFF_SENTENCE,
  people,
  SEND_OFFLINE_SENTENCE,
  sendAllSentence,
  testSentSentence,
} from "../sendCopy";
import { BODY_TEXT } from "../ui";
import { useSendFlow } from "../useSendFlow";

/**
 * The follow-up a few weeks after the September 2026 visit, to everyone
 * who registered for an Australian event of it: the photos are up, the
 * feedback form (which also asks about an online meditation during the
 * week), and the donations line.
 */
export function VisitFollowUpPanel({
  defaultTestEmail,
  senderName,
}: {
  defaultTestEmail: string;
  senderName: string | null;
}) {
  const flow = useSendFlow(defaultTestEmail);
  const audience = trpc.adminVisitFollowUpAudienceCount.useQuery(undefined, {
    retry: false,
    staleTime: 60_000,
  });
  const utils = trpc.useUtils();
  const followUp = trpc.adminSendVisitFollowUp.useMutation();

  const send = (mode: "test" | "all", testEmails: string[]) => {
    followUp.mutate(
      {
        mode,
        testEmails: mode === "test" ? testEmails : undefined,
        testName: mode === "test" ? (senderName ?? undefined) : undefined,
      },
      {
        onSuccess: (res) => {
          if (res.mode === "test") {
            flow.testSent(
              res.skipped ? EMAIL_OFF_SENTENCE : testSentSentence(res.sentTo),
            );
          } else {
            flow.sendAllDone(
              sendAllSentence(
                res,
                "There's no one to send this to, so nothing was sent.",
              ),
            );
            void utils.adminListEmailSends.invalidate();
          }
        },
        onError: (err) =>
          flow.problem(friendlyError(err, SEND_OFFLINE_SENTENCE)),
      },
    );
  };

  const total = audience.data?.total ?? null;
  const countLine = audience.isLoading
    ? "Counting who this goes to…"
    : audience.error || total === null
      ? "Couldn't count who this would go to. Refresh the page before sending."
      : `Goes to ${people(total)}.`;

  return (
    <>
      <p className={BODY_TEXT}>
        Tells everyone who registered for an Australian event of the September
        visit, in Melbourne, Canberra or Sydney, that the photos are on the
        website, asks for their feedback on the form at vyaustralia.org/feedback
        (including whether they would join an online meditation during the week,
        and when), and mentions donations. Christchurch registrants don&apos;t
        get it. Answers show on Event feedback.
      </p>
      <p className="mt-2 text-[15px] text-[color:var(--color-ink)]">
        {countLine}
      </p>

      <TestThenSend
        flow={flow}
        emailName={
          emailTypeLabel("visit_follow_up") ??
          "Photos, feedback and online meditation"
        }
        audience={total !== null && total > 0 ? people(total) : null}
        noAudienceText={
          audience.isLoading
            ? "Counting who this goes to…"
            : audience.error
              ? "Couldn't count who this would go to. Refresh the page before sending."
              : "There's no one to send this to yet."
        }
        multipleAddresses
        busy={followUp.isPending}
        onSendTest={(addresses) => send("test", addresses)}
        onSendAll={() => send("all", [])}
      />
    </>
  );
}
